import React, { Component } from 'react';
import { Button, Jumbotron, ProgressBar } from 'react-bootstrap';

import SystemUtils from '../SystemUtils';

import './CloudUpload.css';

const CHUNK_SIZE = 1048576 * 2; //2MB upload chunks, must stay small enough for the servlet's in-memory buffer
const SPEED_WINDOW_MS = 8000;

//the GeoFuse backend tracks ONE upload globally (upload.UploadState is a
//server-wide singleton), so only one engine may run at a time no matter how
//many CloudUpload instances have been mounted
let globalUploadActive = false;

//recording name a zipped recording upload stands for: 'rec.tar.gz' / 'rec.tgz'
//-> 'rec' (case-insensitive, matching the server's IsArchive); anything that
//is not an archive gives '' so callers can use it as an "is archive" test too
export const archiveRecordingName = (name) => {
    const lower = name.toLowerCase();

    if (lower.endsWith('.tar.gz'))
    {
        return name.substring(0, name.length - '.tar.gz'.length);
    }

    if (lower.endsWith('.tgz'))
    {
        return name.substring(0, name.length - '.tgz'.length);
    }

    return '';
};

class CloudUpload extends Component {

    constructor(props) {
        super(props);

        this.state = {
            progress: 0,
            progressText: '',
            subText: '',
            statusText: '',
            showProgress: false,
            dragActive: false
        };

        //upload engine state lives on the instance, not in this.state: the async
        //loop must keep consistent counters across awaits, and it keeps running
        //just long enough to cancel cleanly if the host Modal unmounts the
        //component mid-transfer
        this.queue = [];
        this.usedNames = Object.create(null); //no prototype, so names like "constructor" don't falsely collide
        this.completedNames = Object.create(null); //relPath -> size of files fully uploaded this session
        this.uploading = false;
        this.cancelRequested = false;
        this.currentRecording = props.recordingAddFile ? props.recordingAddFile : ''; //blank means the first file creates a new recording
        this.totalBytes = 0;
        this.uploadedBytes = 0;
        this.bytesSentSession = 0; //never reset: disk consumed since the free-space snapshot
        this.completedFiles = 0;
        this.totalFiles = 0;
        this.skippedEmpty = 0;
        this.skippedDone = 0;
        this.speedSamples = [];
        this.speedText = '';
        this.activeFilename = '';
        this.batchLabel = ''; //top-level folder name when a single folder is uploaded, used to name the new recording
        this.freeSpace = null;
        this.mounted = false;
        this.dragDepth = 0;

        this.fileInputRef = React.createRef();
        this.folderInputRef = React.createRef();
    }

    componentDidMount() {
        this.mounted = true;

        //stop the browser navigating away when a drop misses the drop zone
        window.addEventListener('dragover', this.preventWindowDrag);
        window.addEventListener('drop', this.preventWindowDrag);

        fetch('system_stats')
        .then(res => res.json())
        .then((data) => {
            this.freeSpace = parseInt(data[0].freeSpace, 10);
        })
        .catch(console.log)
    }

    componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener('dragover', this.preventWindowDrag);
        window.removeEventListener('drop', this.preventWindowDrag);

        //with the popup gone there is no way to watch or cancel the transfer,
        //and a second popup could otherwise start a competing upload against
        //the server's single upload slot
        if (this.uploading)
        {
            this.cancelRequested = true;
        }
    }

    preventWindowDrag = (e) => {
        e.preventDefault();
    }

    safeSetState = (newState) => {
        if (this.mounted)
        {
            this.setState(newState);
        }
    }

    Close = () => {
        if (this.uploading)
        {
            this.cancelRequested = true;
        }
    }

    IsTransferring = () => {
        return this.uploading;
    }

    openFilePicker = () => {
        if (this.fileInputRef.current)
        {
            this.fileInputRef.current.click();
        }
    }

    openFolderPicker = () => {
        if (this.folderInputRef.current)
        {
            this.folderInputRef.current.click();
        }
    }

    handleInputChange = (e) => {
        //folder-picker selections carry webkitRelativePath ("Folder/sub/f.ply"),
        //plain file selections only have a name
        const files = Array.from(e.target.files || []).map((f) => ({
            file: f,
            relPath: (f.webkitRelativePath && f.webkitRelativePath.length > 0) ? f.webkitRelativePath : f.name
        }));
        e.target.value = '';
        this.enqueueFiles(files);
    }

    handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dragDepth++;

        if (!this.state.dragActive)
        {
            this.safeSetState({ dragActive: true });
        }
    }

    handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dragDepth--;

        if (this.dragDepth <= 0)
        {
            this.dragDepth = 0;
            this.safeSetState({ dragActive: false });
        }
    }

    handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dragDepth = 0;
        this.safeSetState({ dragActive: false });

        const dt = e.dataTransfer;

        if (!dt)
        {
            return;
        }

        if (dt.items && dt.items.length > 0 && dt.items[0].webkitGetAsEntry)
        {
            //entries must all be claimed synchronously - the item list is gone
            //once this handler returns
            const entries = [];

            for (let i = 0; i < dt.items.length; i++)
            {
                const entry = dt.items[i].webkitGetAsEntry();

                if (entry)
                {
                    entries.push(entry);
                }
            }

            this.collectEntries(entries).then(files => this.enqueueFiles(files));
        }
        else
        {
            this.enqueueFiles(Array.from(dt.files || []).map((f) => ({ file: f, relPath: f.name })));
        }
    }

    collectEntries = async (entries) => {
        const files = [];

        for (let i = 0; i < entries.length; i++)
        {
            await this.collectEntry(entries[i], '', files);
        }

        return files;
    }

    //prefix carries the relative path of the enclosing folders, so a dropped
    //folder is recreated under the recording rather than flattened into it
    collectEntry = (entry, prefix, files) => {
        return new Promise((resolve) => {
            if (entry.isFile)
            {
                entry.file((file) => { files.push({ file: file, relPath: prefix + file.name }); resolve(); }, () => resolve());
            }
            else if (entry.isDirectory)
            {
                const reader = entry.createReader();
                const childPrefix = prefix + entry.name + '/';

                //readEntries returns at most 100 entries per call and must be
                //re-invoked until it comes back empty
                const readBatch = () => {
                    reader.readEntries(async (batch) => {
                        if (0 === batch.length)
                        {
                            resolve();
                            return;
                        }

                        for (let i = 0; i < batch.length; i++)
                        {
                            await this.collectEntry(batch[i], childPrefix, files);
                        }

                        readBatch();
                    }, () => resolve());
                };

                readBatch();
            }
            else
            {
                resolve();
            }
        });
    }

    //the server appends every chunk to maps/<recording>/<relative path>, so a
    //clashing path within one session is renamed to name(1).ext - a second
    //file with the same path would otherwise be appended onto the first and
    //corrupt it
    uniqueName = (relPath) => {
        let candidate = relPath;
        let n = 1;

        while (this.usedNames[candidate])
        {
            const slash = relPath.lastIndexOf('/');
            const dir = (slash >= 0) ? relPath.substring(0, slash + 1) : '';
            const base = relPath.substring(slash + 1);
            const dot = base.lastIndexOf('.');
            const stem = (dot > 0) ? base.substring(0, dot) : base;
            const ext = (dot > 0) ? base.substring(dot) : '';

            candidate = dir + stem + '(' + n + ')' + ext;
            n++;
        }

        this.usedNames[candidate] = true;
        return candidate;
    }

    enqueueFiles = (files) => {
        console.log('CloudUpload: enqueueFiles received ' + (files ? files.length : 0) + ' file(s)');

        //a dropped folder is scanned asynchronously - if the popup was closed
        //before the scan finished, starting a headless upload now would be
        //invisible and uncancellable
        if (!this.mounted)
        {
            console.log('CloudUpload: popup closed before the selection resolved - ignoring');
            return;
        }

        //an empty selection must not fail silently - a cancelled picker,
        //an empty folder or a failed folder scan all end up here
        if (!files || 0 === files.length)
        {
            this.safeSetState({ statusText: 'No files were found in the selection - nothing was uploaded' });
            return;
        }

        //another popup's engine still owns the server's single upload slot
        //(possibly draining after its popup closed)
        if (globalUploadActive && !this.uploading)
        {
            console.log('CloudUpload: refused - another upload engine is still active');
            this.safeSetState({ statusText: 'Another upload is still finishing - please try again in a moment' });
            return;
        }

        const accepted = [];
        let acceptedBytes = 0;

        for (let i = 0; i < files.length; i++)
        {
            if (0 === files[i].file.size)
            {
                this.skippedEmpty++;
            }
            else if (this.completedNames[files[i].relPath] === files[i].file.size)
            {
                this.skippedDone++; //identical file already fully uploaded this session (e.g. retry after a failure)
            }
            else
            {
                accepted.push(files[i]);
                acceptedBytes += files[i].file.size;
            }
        }

        if (0 === accepted.length)
        {
            const reason = (this.skippedDone > 0) ? 'these files have already been uploaded' : 'empty files are skipped';
            console.log('CloudUpload: refused - ' + reason);
            this.safeSetState({ statusText: 'Nothing to upload - ' + reason });
            return;
        }

        //everything sent since the free-space snapshot still occupies the disk,
        //including earlier failed or cancelled batches
        const needed = this.bytesSentSession + (this.totalBytes - this.uploadedBytes) + acceptedBytes;

        if (null !== this.freeSpace && !isNaN(this.freeSpace) && needed > this.freeSpace)
        {
            console.log('CloudUpload: refused - free space ' + this.freeSpace + ' bytes, needed ' + needed);
            this.safeSetState({ statusText: 'Not enough free disk space - this session needs ' + SystemUtils.GetSizeString(needed) + ' but only ' + SystemUtils.GetSizeString(this.freeSpace) + ' was free' });
            return;
        }

        for (let i = 0; i < accepted.length; i++)
        {
            this.queue.push({ file: accepted[i].file, name: this.uniqueName(accepted[i].relPath), relPath: accepted[i].relPath });
        }

        this.totalBytes += acceptedBytes;
        this.totalFiles += accepted.length;

        this.safeSetState({ showProgress: true, statusText: '' });
        this.updateProgressUi();

        if (!this.uploading)
        {
            //only a brand new recording (main Upload button) takes the dropped
            //folder's name with its contents at the recording root; the Add
            //File flow keeps the folder as a subfolder inside the existing
            //recording, so no label and no prefix stripping there
            this.batchLabel = ('' === this.currentRecording) ? this.getCommonTopFolder(this.queue) : '';

            //a lone zipped recording names the new recording after itself: the
            //server extracts it in place on cloud_stop_upload, so the DB row is
            //already called after the recording rather than a timestamp
            if ('' === this.currentRecording && '' === this.batchLabel && 1 === this.queue.length && -1 === this.queue[0].relPath.indexOf('/'))
            {
                this.batchLabel = archiveRecordingName(this.queue[0].relPath);
            }

            this.uploading = true;
            globalUploadActive = true;
            this.cancelRequested = false;
            this.runQueue();
        }
    }

    //when every queued file sits under the same top-level folder, that folder
    //names the new recording (2026_07_31_10_54_24_BCC) so the upload is
    //recognisable in the recordings list; mixed or file-only batches get none
    getCommonTopFolder = (items) => {
        let top = '';

        for (let i = 0; i < items.length; i++)
        {
            const slash = items[i].relPath.indexOf('/');

            if (slash <= 0)
            {
                return '';
            }

            const folder = items[i].relPath.substring(0, slash);

            if ('' === top)
            {
                top = folder;
            }
            else if (top !== folder)
            {
                return '';
            }
        }

        return top;
    }

    runQueue = async () => {
        this.resetSpeed();

        let currentName = '';

        try
        {
            while (this.queue.length > 0 && !this.cancelRequested)
            {
                const item = this.queue[0];
                currentName = item.name;

                const result = await this.uploadFile(item);

                if ('cancelled' === result)
                {
                    break;
                }

                //the batch is already finished with the reason on screen
                if ('failed' === result)
                {
                    return;
                }

                this.queue.shift();
                this.completedFiles++;
            }

            if (this.cancelRequested)
            {
                await this.sendCancel();
                this.releaseUnstartedNames();
                this.finishBatch(0, 'Upload Cancelled');
                return;
            }

            let msg = 'Upload Complete';

            if (this.skippedEmpty > 0)
            {
                msg += ' - ' + this.skippedEmpty + ' empty file(s) skipped';
            }

            if (this.skippedDone > 0)
            {
                msg += ' - ' + this.skippedDone + ' already-uploaded file(s) skipped';
            }

            this.finishBatch(100, msg);

            if (this.mounted)
            {
                this.props.handleCloseUpload();
            }
        }
        catch (error)
        {
            console.log('upload error', error);
            this.releaseUnstartedNames();
            this.finishBatch(0, 'Upload failed: ' + currentName);
        }
    }

    //frees the claimed names of queued files that never started, so a retry
    //drop reuses their original names; queue[0] stays claimed because a
    //partial copy of it may exist on the server and appending to it - or to
    //any fully uploaded file - would corrupt it
    releaseUnstartedNames = () => {
        for (let i = 1; i < this.queue.length; i++)
        {
            delete this.usedNames[this.queue[i].name];
        }
    }

    //one file at a time: the backend tracks a single global upload and blindly
    //appends each cloud_upload chunk, so chunks and files must stay sequential
    uploadFile = async (item) => {
        const file = item.file;

        //the recording folder IS the uploaded folder (named via recordingName),
        //so files from that folder upload without its prefix and land directly
        //in the recording; files dropped later from other folders keep theirs
        let uploadName = item.name;

        if (this.batchLabel.length > 0 && 0 === uploadName.indexOf(this.batchLabel + '/'))
        {
            uploadName = uploadName.substring(this.batchLabel.length + 1);
        }

        this.activeFilename = uploadName;
        this.updateProgressUi();

        if (this.cancelRequested)
        {
            return 'cancelled';
        }

        const startRes = await fetch('cloud_start_upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: 'filename=' + encodeURIComponent(uploadName) + '&filesize=' + file.size + '&recording=' + encodeURIComponent(this.currentRecording) + '&recordingName=' + encodeURIComponent(this.batchLabel)
        });
        const startData = await startRes.json();

        if ('true' !== String(startData.success))
        {
            throw new Error('cloud_start_upload rejected: ' + startData.msg);
        }

        if (startData.recording && startData.recording.length > 0)
        {
            this.currentRecording = startData.recording;
        }

        const chunkTotal = Math.ceil(file.size / CHUNK_SIZE);

        for (let chunkId = 0; chunkId < chunkTotal; chunkId++)
        {
            if (this.cancelRequested)
            {
                return 'cancelled';
            }

            const startByte = chunkId * CHUNK_SIZE;
            const endByte = Math.min(startByte + CHUNK_SIZE, file.size);

            //cloud_upload parses the multipart body with fixed byte offsets
            //that assume a WebKit-style 38 character boundary, so build the
            //body by hand - every browser then produces the exact layout the
            //servlet expects (Firefox's own boundaries have a different length
            //and would corrupt every chunk)
            const boundary = this.makeBoundary();
            const head = '--' + boundary + '\r\nContent-Disposition: form-data; name="file"; filename="blob"\r\nContent-Type: application/octet-stream\r\n\r\n';
            const tail = '\r\n--' + boundary + '--\r\n';
            const body = new Blob([head, file.slice(startByte, endByte), tail]);

            const res = await fetch('cloud_upload', {
                method: 'POST',
                headers: {
                    'Content-Type': 'multipart/form-data; boundary=' + boundary
                },
                body: body
            });

            if (!res.ok)
            {
                throw new Error('cloud_upload failed with status ' + res.status);
            }

            await res.json();

            this.uploadedBytes += (endByte - startByte);
            this.bytesSentSession += (endByte - startByte);
            this.recordSpeedSample();
            this.updateProgressUi();
        }

        //cloud_stop_upload extracts a zipped recording before it answers, so
        //the bar would otherwise sit at 100% with no explanation
        if (archiveRecordingName(uploadName).length > 0)
        {
            this.safeSetState({ subText: 'Extracting ' + uploadName + ' on the server - this can take a while for large recordings' });
        }

        const stopRes = await fetch('cloud_stop_upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: 'recording=' + encodeURIComponent(this.currentRecording)
        });

        if (!stopRes.ok)
        {
            throw new Error('cloud_stop_upload failed with status ' + stopRes.status);
        }

        const stopData = await stopRes.json();

        //an archive must be extracted server-side to become a usable recording. Require an
        //explicit archiveExtracted:true; a false means extraction failed, and a missing field
        //means an older backend stored the .tar.gz without unpacking it (equally unusable).
        //Either way stop the batch here with the reason and leave the popup open to read it.
        if (archiveRecordingName(uploadName).length > 0 && true !== stopData.archiveExtracted)
        {
            const reason = (undefined === stopData.archiveExtracted)
                ? 'this server does not support zipped recording uploads'
                : stopData.msg;

            this.releaseUnstartedNames();
            this.finishBatch(0, 'Upload failed: ' + uploadName + ' - ' + reason);
            return 'failed';
        }

        this.completedNames[item.relPath] = file.size;

        return 'done';
    }

    //22 fixed chars + 16 random = the 38 character boundary CloudUpload.java's
    //offset arithmetic (+42 head, -88 tail) is hardcoded against
    makeBoundary = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let ret = '----WebKitFormBoundary';

        for (let i = 0; i < 16; i++)
        {
            ret += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        return ret;
    }

    sendCancel = async () => {
        try
        {
            await fetch('cloud_cancel_upload', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: 'recording=' + encodeURIComponent(this.currentRecording)
            });
        }
        catch (error)
        {
            console.log('cancel error', error);
        }
    }

    finishBatch = (progressValue, message) => {
        this.uploading = false;
        globalUploadActive = false;
        this.cancelRequested = false;
        this.queue = [];
        this.totalBytes = 0;
        this.uploadedBytes = 0;
        this.completedFiles = 0;
        this.totalFiles = 0;
        this.skippedEmpty = 0;
        this.skippedDone = 0;
        this.activeFilename = '';
        this.speedSamples = [];
        this.speedText = '';

        this.safeSetState({ progress: progressValue, progressText: message, subText: '', statusText: '' });
    }

    resetSpeed = () => {
        this.speedSamples = [{ t: Date.now(), bytes: this.uploadedBytes }];
        this.speedText = '';
    }

    recordSpeedSample = () => {
        const now = Date.now();

        this.speedSamples.push({ t: now, bytes: this.uploadedBytes });

        while (this.speedSamples.length > 2 && (now - this.speedSamples[0].t) > SPEED_WINDOW_MS)
        {
            this.speedSamples.shift();
        }

        const first = this.speedSamples[0];
        const seconds = (now - first.t) / 1000.0;
        const bytes = this.uploadedBytes - first.bytes;

        if (seconds > 0.2 && bytes > 0)
        {
            this.speedText = SystemUtils.GetSizeString(bytes / seconds) + '/s';
        }
    }

    updateProgressUi = () => {
        const pct = (this.totalBytes > 0) ? (100.0 * this.uploadedBytes / this.totalBytes) : 0;

        //percent and speed lead so a long filename truncates off the right
        //edge instead of hiding them; the bar shows the base name and the
        //sub-line below carries the full relative path
        const baseName = this.activeFilename.substring(this.activeFilename.lastIndexOf('/') + 1);

        let label = pct.toFixed(1) + '%';

        if (this.speedText.length > 0)
        {
            label += ' | ' + this.speedText;
        }

        if (baseName.length > 0)
        {
            label += ' | ' + baseName;
        }

        const subText = 'File ' + Math.min(this.completedFiles + 1, this.totalFiles) + ' of ' + this.totalFiles
            + ' - ' + this.activeFilename
            + ' - ' + SystemUtils.GetSizeString(this.uploadedBytes) + ' / ' + SystemUtils.GetSizeString(this.totalBytes);

        this.safeSetState({ progress: pct, progressText: label, subText: subText, showProgress: true });
    }

    render() {
        const blackStyle = {
            color: "black",
        };

        return (<Jumbotron className="jmbUpload">
                <div
                    className={'uploadDropZone' + (this.state.dragActive ? ' uploadDropZoneActive' : '')}
                    onDragEnter={this.handleDragEnter}
                    onDragOver={this.handleDragOver}
                    onDragLeave={this.handleDragLeave}
                    onDrop={this.handleDrop}
                >
                    <div className="uploadDropText" style={blackStyle}>Drag &amp; drop files or folders here</div>
                    <div className="uploadDropText" style={blackStyle}>or</div>
                    <div className="uploadDropButtons">
                        <Button className="btnUpload" onClick={this.openFilePicker}>Choose Files</Button>
                        <Button className="btnUpload" onClick={this.openFolderPicker}>Choose Folder</Button>
                    </div>
                    <input
                        id="frmFile"
                        type="file"
                        multiple
                        accept=".ply, .las, .mp4, .txt, .tar.gz, .tgz | pointcloud/*"
                        ref={this.fileInputRef}
                        style={{ display: 'none' }}
                        onChange={this.handleInputChange}
                    />
                    <input
                        type="file"
                        multiple
                        webkitdirectory=""
                        directory=""
                        ref={this.folderInputRef}
                        style={{ display: 'none' }}
                        onChange={this.handleInputChange}
                    />
                </div>

                <div className="uploadProgressSection" style={{ display: this.state.showProgress ? 'block' : 'none' }}>
                    <div className="uploadBarWrap">
                        <ProgressBar className="prgUpload" animated now={this.state.progress} />
                        <div className="uploadBarLabel">{this.state.progressText}</div>
                    </div>
                    <div className="uploadSubText" style={blackStyle}>{this.state.subText}</div>
                </div>

                {this.state.statusText.length > 0 &&
                    <div className="uploadStatusText">{this.state.statusText}</div>
                }
                </Jumbotron>)
    }
}

export default CloudUpload;
